/**
 * Comparison View - Side-by-side comparison of two choices
 *
 * Optimized for quick decision making during a Blood Bowl game.
 * Shows both choices with their probabilities prominently displayed.
 */

import { cn } from "@/lib/utils";
import type { ComparisonChoice, CalculationResults } from "../types";
import { ProbabilityDisplay } from "./ProbabilityDisplay";
import { RiskIndicator } from "./RiskIndicator";
import { useLanguage } from "../i18n";

export interface ComparisonViewProps {
  choices: [ComparisonChoice, ComparisonChoice];
  activeChoiceIndex: 0 | 1;
  teamRerollsAvailable: number;
  onSelectChoice: (index: 0 | 1) => void;
  className?: string;
}

function ChoiceCard({
  choice,
  isActive,
  isWinner,
  teamRerollsAvailable,
  onClick,
  probDiff,
}: {
  choice: ComparisonChoice;
  isActive: boolean;
  isWinner: boolean;
  teamRerollsAvailable: number;
  onClick: () => void;
  probDiff: number | null;
}) {
  const { t } = useLanguage();
  const hasResults = choice.results !== null;
  const hasActions = choice.actionGroups.some((g) => g.actions.length > 0);

  return (
    <button
      onClick={onClick}
      className={cn(
        "flex-1 min-w-0 p-4 sm:p-6 rounded-lg transition-all duration-200",
        "border-2 focus:outline-none focus:ring-2 focus:ring-bb-gold/50",
        "hover:scale-[1.02] active:scale-[0.98]",
        isActive
          ? "border-bb-gold bg-bb-gray-dark shadow-lg shadow-bb-gold/20"
          : "border-bb-gray-mid bg-bb-gray-dark/60 hover:border-bb-gold/50",
        isWinner && hasResults && "ring-2 ring-green-500/50"
      )}
    >
      {/* Choice Label */}
      <div className="flex items-center justify-between mb-4">
        <span
          className={cn(
            "text-2xl sm:text-3xl font-condensed font-bold uppercase tracking-wider",
            isActive ? "text-bb-gold" : "text-bb-bone-white/70"
          )}
        >
          {choice.label}
        </span>
        {isActive && (
          <span className="text-xs uppercase tracking-wider text-bb-gold/70 bg-bb-gold/10 px-2 py-1 rounded">
            {t.comparison.editing}
          </span>
        )}
      </div>

      {/* Probability Display */}
      {hasResults ? (
        <div className="space-y-3">
          <ProbabilityDisplay
            probability={choice.results!.totalProbability}
            size="compact"
          />

          {/* Difference indicator */}
          {probDiff !== null && probDiff !== 0 && (
            <div
              className={cn(
                "text-sm font-mono font-bold",
                probDiff > 0 ? "text-green-400" : "text-red-400"
              )}
            >
              {probDiff > 0 ? "+" : ""}{probDiff.toFixed(1)}%
            </div>
          )}

          <RiskIndicator
            probability={choice.results!.totalProbability}
            compact
          />

          <div className="text-xs text-bb-bone-white/50 mt-2">
            {t.teamRerollsUsed}: {choice.results!.teamRerollsUsed}/{teamRerollsAvailable}
          </div>
        </div>
      ) : (
        <div className="py-8 text-center">
          {hasActions ? (
            <span className="text-bb-bone-white/40 text-sm">
              {t.comparison.calculating}
            </span>
          ) : (
            <span className="text-bb-bone-white/40 text-sm">
              {t.comparison.noActions}
            </span>
          )}
        </div>
      )}

      {/* Action count indicator */}
      <div className="mt-4 pt-3 border-t border-bb-gray-mid/50">
        <span className="text-xs text-bb-bone-white/40">
          {choice.actionGroups.reduce((sum, g) => sum + g.actions.length, 0)} {t.comparison.actions}
        </span>
      </div>
    </button>
  );
}

export function ComparisonView({
  choices,
  activeChoiceIndex,
  teamRerollsAvailable,
  onSelectChoice,
  className,
}: ComparisonViewProps) {
  const { t } = useLanguage();

  // Calculate which choice is the winner
  const probA = choices[0].results?.totalProbability ?? null;
  const probB = choices[1].results?.totalProbability ?? null;

  let winnerIndex: 0 | 1 | null = null;
  let probDiffA: number | null = null;
  let probDiffB: number | null = null;

  if (probA !== null && probB !== null) {
    if (probA > probB) {
      winnerIndex = 0;
      probDiffA = probA - probB;
      probDiffB = probB - probA;
    } else if (probB > probA) {
      winnerIndex = 1;
      probDiffA = probA - probB;
      probDiffB = probB - probA;
    }
  }

  return (
    <section className={cn("mb-6", className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-condensed text-lg font-bold uppercase tracking-wider text-bb-gold">
          {t.comparison.title}
        </h2>
        {winnerIndex !== null && (
          <span className="text-xs uppercase tracking-wider text-green-400 bg-green-400/10 px-2 py-1 rounded flex items-center gap-1">
            <span className="text-lg">&#x2713;</span>
            {t.comparison.winner}: {choices[winnerIndex].label}
          </span>
        )}
      </div>

      {/* Side-by-side comparison */}
      <div className="flex gap-3 sm:gap-4">
        <ChoiceCard
          choice={choices[0]}
          isActive={activeChoiceIndex === 0}
          isWinner={winnerIndex === 0}
          teamRerollsAvailable={teamRerollsAvailable}
          onClick={() => onSelectChoice(0)}
          probDiff={probDiffA}
        />

        {/* VS Divider */}
        <div className="flex items-center justify-center">
          <span className="text-bb-blood-red font-condensed font-bold text-xl sm:text-2xl">
            VS
          </span>
        </div>

        <ChoiceCard
          choice={choices[1]}
          isActive={activeChoiceIndex === 1}
          isWinner={winnerIndex === 1}
          teamRerollsAvailable={teamRerollsAvailable}
          onClick={() => onSelectChoice(1)}
          probDiff={probDiffB}
        />
      </div>

      {/* Quick tip */}
      <p className="text-center text-xs text-bb-bone-white/30 mt-3">
        {t.comparison.tip}
      </p>
    </section>
  );
}
